import { useEffect } from 'react';
import {
  hasUnsavedChanges,
  subscribeUnsavedChanges,
} from './unsavedChangesAuthority';

let registration: Promise<void> | null = null;
let pendingUpdate: ((reloadPage?: boolean) => Promise<void>) | null = null;
let updateInFlight = false;
let updateFlushQueued = false;

function registerServiceWorkerOnce(): Promise<void> {
  if (registration) {
    return registration;
  }

  const attempt = import('virtual:pwa-register')
    .then(({ registerSW }) => {
      let applyUpdate: ReturnType<typeof registerSW> | null = null;
      let refreshRequested = false;
      const handleNeedRefresh = () => {
        refreshRequested = true;
        if (applyUpdate) queueWaitingUpdate(applyUpdate);
      };

      applyUpdate = registerSW({
        onNeedRefresh: handleNeedRefresh,
        onRegisterError: () => {
          if (registration === attempt) {
            registration = null;
          }
        },
      });
      if (refreshRequested) queueWaitingUpdate(applyUpdate);
    })
    .catch(() => {
      if (registration === attempt) {
        registration = null;
      }
      /* dev mode or a transient deploy gap: retry on the next mount/visibility/online event */
    });
  registration = attempt;
  return attempt;
}

function queueWaitingUpdate(
  applyUpdate: (reloadPage?: boolean) => Promise<void>,
): void {
  pendingUpdate = applyUpdate;
  queueUpdateFlush();
}

function queueUpdateFlush(): void {
  if (updateFlushQueued) return;
  updateFlushQueued = true;
  queueMicrotask(() => {
    updateFlushQueued = false;
    void applyPendingUpdateIfClean();
  });
}

async function applyPendingUpdateIfClean(): Promise<void> {
  if (updateInFlight || hasUnsavedChanges() || !pendingUpdate) return;

  const applyUpdate = pendingUpdate;
  pendingUpdate = null;
  updateInFlight = true;
  try {
    await applyUpdate(true);
  } catch {
    pendingUpdate ??= applyUpdate;
  } finally {
    updateInFlight = false;
  }
}

async function updateServiceWorkerIfAvailable(): Promise<void> {
  if (document.visibilityState !== 'visible' || !('serviceWorker' in navigator)) {
    return;
  }

  try {
    const activeRegistration = await navigator.serviceWorker.getRegistration();
    await activeRegistration?.update();
  } catch {
    /* Browsers may reject while offline; the next visibility event retries. */
  }
}

/** Mount the console's sole service-worker registration path. */
export function usePwaUpdater(): void {
  useEffect(() => {
    void registerServiceWorkerOnce();
    queueUpdateFlush();

    const unsubscribeUnsavedChanges = subscribeUnsavedChanges(queueUpdateFlush);

    const handleRetryOpportunity = () => {
      if (document.visibilityState === 'visible') {
        void registerServiceWorkerOnce()
          .then(updateServiceWorkerIfAvailable)
          .finally(queueUpdateFlush);
      }
    };
    document.addEventListener('visibilitychange', handleRetryOpportunity);
    window.addEventListener('online', handleRetryOpportunity);
    return () => {
      unsubscribeUnsavedChanges();
      document.removeEventListener('visibilitychange', handleRetryOpportunity);
      window.removeEventListener('online', handleRetryOpportunity);
    };
  }, []);
}
